import { useAuth } from '../context/AuthContext'

const API_BASE = import.meta.env.VITE_API_BASE || '/v1/redeem'

export const useApi = () => {
  const { getAuthHeader } = useAuth()

  const apiCall = async (endpoint, options = {}) => {
    const url = `${API_BASE}${endpoint}`
    const authHeaders = getAuthHeader()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers
      },
      ...options
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        const errorData = await response.text()
        let errorMessage = `HTTP ${response.status}`
        
        try {
          const errorJson = JSON.parse(errorData)
          errorMessage = errorJson.message || (typeof errorJson.error === 'string' ? errorJson.error : null) || errorMessage
        } catch {
          errorMessage = errorData || errorMessage
        }
        
        throw new Error(errorMessage)
      }

      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        return await response.json()
      }
      
      return await response.text()
    } catch (error) {
      console.error('API call failed:', error)
      throw error
    }
  }

  // Public API methods
  const getProducts = () => apiCall('/products')
  
  const validateCode = (code) => apiCall('/validate', {
    method: 'POST',
    body: JSON.stringify({ code })
  })
  
  const validateUser = (code, userInput) => apiCall('/validate-user', {
    method: 'POST',
    body: JSON.stringify({ code, user_input: userInput })
  })
  
  const confirmRedemption = (code, userInput, captchaToken) => apiCall('/confirm', {
    method: 'POST',
    body: JSON.stringify({
      code,
      user_input: userInput,
      captcha_token: captchaToken
    })
  })
  
  const getRedemptionStatus = (redemptionId) => apiCall(`/status/${redemptionId}`)

  // Admin API methods
  const adminLogin = async (username, password, totpCode) => {
    const url = `${API_BASE}/admin/auth/login`
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, ...(totpCode ? { totp_code: totpCode } : {}) })
    })
    const data = await res.json()
    if (res.status === 401) throw new Error(data.message || 'Invalid credentials')
    return data
  }

  const getAdminProducts = () => apiCall('/admin/products')
  
  const createProduct = (product) => apiCall('/admin/products', {
    method: 'POST',
    body: JSON.stringify(product)
  })
  
  const updateProduct = (id, product) => apiCall(`/admin/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(product)
  })
  
  const deleteProduct = (id) => apiCall(`/admin/products/${id}`, {
    method: 'DELETE'
  })

  const getG2BulkCatalog = () => apiCall('/admin/g2bulk/catalog')
  
  const generateCodes = (productId, count, batchId, expiresAt) => apiCall('/admin/codes/generate', {
    method: 'POST',
    body: JSON.stringify({
      product_id: productId,
      count,
      batch_id: batchId,
      expires_at: expiresAt
    })
  })
  
  const getCodes = (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return apiCall(`/admin/codes${query ? `?${query}` : ''}`)
  }
  
  const revokeCode = (id) => apiCall(`/admin/codes/${id}/revoke`, {
    method: 'PATCH'
  })

  const deleteCode = (id) => apiCall(`/admin/codes/${id}`, {
    method: 'DELETE'
  })
  
  const bulkRevokeCodes = (batchId) => apiCall('/admin/codes/bulk-revoke', {
    method: 'POST',
    body: JSON.stringify({ batch_id: batchId })
  })
  
  const getStats = () => apiCall('/admin/stats')
  
  const getRedemptions = (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return apiCall(`/admin/redemptions${query ? `?${query}` : ''}`)
  }
  
  const getRedemption = (id) => apiCall(`/admin/redemptions/${id}`)
  
  const getAuditLog = (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return apiCall(`/admin/audit${query ? `?${query}` : ''}`)
  }

  return {
    // Public
    getProducts,
    validateCode,
    validateUser,
    confirmRedemption,
    getRedemptionStatus,
    
    // Admin auth
    adminLogin,
    
    // Admin operations
    getAdminProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    getG2BulkCatalog,
    generateCodes,
    getCodes,
    revokeCode,
    deleteCode,
    bulkRevokeCodes,
    getStats,
    getRedemptions,
    getRedemption,
    getAuditLog
  }
}